import { persistSsoAuth, type AuthState } from './login'
import { apiOrigin } from '@/shared/config/endpoints'

/**
 * Azure AD / SAML SSO via the Office Dialog API.
 *
 * Mirrors ramp-web-app's Login: it POSTs `username` + `redirectUrl` to
 * `{API}/auth/authentication/{provider}`, the IdP authenticates, and the backend
 * redirects to `redirectUrl` with the auth payload. In a task pane we can't do a
 * full-page navigation, so we open the flow in an Office dialog:
 *
 *   sso-start.html (our origin) --auto POST--> {API}/auth/authentication/{provider}
 *     --> IdP --> backend --> sso-complete.html (our origin) --messageParent(token)-->
 *
 * sso-complete.html runs on the add-in's own origin, so `messageParent` is allowed.
 *
 * BACKEND/IdP REQUIREMENTS (so the token reaches our page on localhost):
 *  - The IdP must allow the add-in redirect URL (https://localhost:3099/sso-complete.html).
 *  - The backend must return the token to `redirectUrl` in the URL (query/hash) — the
 *    web app reads it from a parent-domain cookie (.staging-filingramp.com), which is
 *    not readable from the add-in origin. sso-complete.html also tries the cookie as
 *    a fallback for same-domain deployments.
 */

// SSO must hit the real API origin (cookie/redirect domains matter) — NOT the Vite proxy.
const SSO_API = apiOrigin || 'https://api.filingramp.com'
// Normalize to include the /v1 path segment the auth endpoints live under.
const SSO_API_BASE = /\/v\d+$/.test(SSO_API) ? SSO_API : `${SSO_API.replace(/\/$/, '')}/v1`

export function startSso(provider: 'azure' | 'saml', email: string): Promise<AuthState> {
  return new Promise((resolve, reject) => {
    const ui = Office?.context?.ui
    if (!ui?.displayDialogAsync) {
      reject(new Error('SSO requires running inside an Office host.'))
      return
    }

    const completeUrl = `${window.location.origin}/sso-complete.html`
    const startUrl =
      `${window.location.origin}/sso-start.html?` +
      new URLSearchParams({
        provider,
        username: email,
        api: SSO_API_BASE,
        redirectUrl: completeUrl
      }).toString()

    ui.displayDialogAsync(startUrl, { height: 60, width: 30, promptBeforeOpen: false }, (open) => {
      if (open.status !== Office.AsyncResultStatus.Succeeded) {
        reject(new Error(open.error?.message || 'Could not open the SSO window.'))
        return
      }
      const dialog = open.value

      dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
        try {
          const payload = JSON.parse((arg as { message: string }).message)
          if (payload?.error) throw new Error(payload.error)
          const state = persistSsoAuth(payload)
          dialog.close()
          resolve(state)
        } catch (e) {
          dialog.close()
          reject(e instanceof Error ? e : new Error('SSO sign-in failed.'))
        }
      })

      dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
        // 12006 = user closed the dialog.
        const code = (arg as { error?: number }).error
        reject(new Error(code === 12006 ? 'SSO window was closed.' : 'SSO window error.'))
      })
    })
  })
}

/** Open an external URL (forgot password, etc.) in the system browser. */
export function openExternal(url: string): void {
  const ui = Office?.context?.ui as { openBrowserWindow?: (u: string) => void } | undefined
  if (ui?.openBrowserWindow) ui.openBrowserWindow(url)
  else window.open(url, '_blank', 'noopener')
}

const CLIENT_URL = import.meta.env.VITE_CLIENT_URL || 'https://app.staging-filingramp.com'
export const FORGOT_PASSWORD_URL = `${CLIENT_URL.replace(/\/$/, '')}/forgot-password`
